// /src/pages/Survey/SurveyPage.jsx

import { useMemo, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import SurveyItem from '../../components/SurveyItem/SurveyItem';
import {
  updateSector,
  updateShareWith
} from '../../api/digiscore-api';

import './SurveyPage.css';
import EditProfile from '../../components/EditProfile/EditProfile';
import CompareSession from '../../components/CompareSession/CompareSession';
import ShareSession from '../../components/ShareSession/ShareSession';
import SurveyDialog from '../../components/SurveyDialog/SurveyDialog';
import Loading from '../../components/Loading/Loading';

import { usePartnersList } from '../../hook/usePartnersList';
import { useSectorsCache } from '../../hook/useSectorsCache';
import { getSurveySessionsPage } from '../../api/digiscore-api';
import StatusFilterButton from '../../components/StatusFilterButton';

function SurveyPage(props) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const PAGE_LIMIT = 15;

  const [page, setPage] = useState(1);
  const [pagedItems, setPagedItems] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [currentBce, setCurrentBce] = useState();
  const [currentSector, setCurrentSector] = useState('');
  const [currentEnterpriseName, setCurrentEnterpriseName] = useState();
  const [currentSessionId, setCurrentSessionId] = useState();
  const [currentSharedWith, setCurrentSharedWith] = useState();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(false);
  const [dialogContent, setDialogContent] = useState('compare');
  const [comparedSessionId, setComparedSessionId] = useState();
  const [newSelectedSector, setNewSelectedSector] = useState();
  const [newPartners, setNewPartners] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const [debouncedQuery, setDebouncedQuery] = useState('');
  const listTopRef = useRef(null);
  const headerRef = useRef(null);
  const hasVisited = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 1500);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const statuses = useMemo(() => ([
    { value: 'completed', label: t('survey.list.completed') },
    { value: 'not-started', label: t('survey.list.not-started') },
    { value: 'in-progress', label: t('survey.list.started') }
  ]), [t]);

  const { partners } = usePartnersList(props.token);
  const {
    sectorsByBce,
    ensureSectors,
    isLoading: isSectorsLoading,
    error: sectorsError
  } = useSectorsCache(props.token);

  const fetchPage = async (p = 1) => {
    if (!isAuthenticated) return;

    setIsLoading(true);

    try {
      const data = await getSurveySessionsPage(user, props.token, {
        page: p,
        pageSize: PAGE_LIMIT,
        q: debouncedQuery,
        statuses: selectedStatuses
      });

      setPagedItems(data.items || []);
      setTotalResults(data.total || 0);
    } catch (e) {
      console.error(e);
      setPagedItems([]);
      setTotalResults(0);
    } finally {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    fetchPage(1).then(() => {
      hasVisited.current = true;
    });
  // eslint-disable-next-line
  }, [debouncedQuery, selectedStatuses, isAuthenticated]);

  function toggleStatus(value) {
    setSelectedStatuses(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  }

  function closeDialog() {
    setDialogError(undefined);
    setDialogOpen(false);
    fetchPage(page);
  }

  const handleCompareSession = (sessionId, name) => {
    setCurrentSessionId(sessionId);
    setDialogContent('compare');
    setCurrentEnterpriseName(name);
    setDialogOpen(true);
  };

  const handleEditProfile = async (bce, sector, sessionId) => {
    setCurrentBce(bce);
    setCurrentSessionId(sessionId);
    setCurrentSector(sector);
    setDialogContent('edit');
    setDialogOpen(true);
    try {
      setDialogLoading(true);
      await ensureSectors(bce);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleShareSession = (sessionId, name, sharedWith) => {
    setCurrentSessionId(sessionId);
    setCurrentEnterpriseName(name);
    setCurrentSharedWith(sharedWith);
    setDialogContent('share');
    setDialogOpen(true);
  };

  const handleModifySession = async (sessionId) => {
    navigate(`/survey/${sessionId}`, {
      replace: false,
      state: { from: '/survey' }
    });
  };

  function compareSessionConfirmHandler() {
    if (currentSessionId && comparedSessionId) {
      navigate(`result/CUR=${currentSessionId}&COMP=${comparedSessionId}`);
    }
  }

  async function editSessionConfirmHandler() {
    try {
      setDialogLoading(true);
      await updateSector(currentBce, newSelectedSector, currentSessionId, props.token);
      setDialogOpen(false);
      fetchPage(page);
    } catch (error) {
      console.error(error);
    } finally {
      setDialogLoading(false);
    }
  }

  async function shareSessionConfirmHandler() {
    try {
      setDialogLoading(true);
      await updateShareWith(currentSessionId, newPartners, props.token);
      setDialogOpen(false);
      fetchPage(page);
    } catch (error) {
      console.error(error);
    } finally {
      setDialogLoading(false);
    }
  }

  function compareSessionDialog() {
    const selectSessionHandler = (sessionId) => setComparedSessionId(sessionId);
    return (
      <CompareSession
        surveys={
          pagedItems
            .map(enterprise => {
              const filtered = (enterprise.surveySessions || []).filter(
                s => s.status === 'completed' && s._id !== currentSessionId
              );
              if (filtered.length > 0) {
                return { ...enterprise, surveySessions: filtered };
              }
              return undefined;
            })
            .filter(Boolean)
        }
        sessionId={currentSessionId}
        selectSessionHandler={selectSessionHandler}
        enterpriseName={currentEnterpriseName}
      />
    );
  }

  function editSessionDialog() {
    const selectSectorHandler = (newSector) => setNewSelectedSector(newSector);
    const list = sectorsByBce[currentBce] || [];
    return (
      <EditProfile
        defaultSector={currentSector}
        sectors={list}
        selectSectorHandler={selectSectorHandler}
      />
    );
  }

  function shareSessionDialog() {
    const selectPartnersHandler = (newPartnersSelected) => setNewPartners(newPartnersSelected);
    return (
      <ShareSession
        partners={partners}
        initPartners={currentSharedWith}
        enterpriseName={currentEnterpriseName}
        selectPartnersHandler={selectPartnersHandler}
      />
    );
  }

  function range(start, end) {
    const out = [];
    for (let i = start; i <= end; i++) out.push(i);
    return out;
  }
  
  function buildPageItems(totalPages, current, { boundaryCount = 1, siblingCount = 1 } = {}) {
    if (totalPages <= 0) return [];

    const startPages = range(1, Math.min(boundaryCount, totalPages));
    const endPages = range(
      Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
      totalPages
    );

    const siblingsStart = Math.max(
      Math.min(
        current - siblingCount,
        totalPages - boundaryCount - siblingCount * 2 - 1
      ),
      boundaryCount + 2
    );

    const siblingsEnd = Math.min(
      Math.max(
        current + siblingCount,
        boundaryCount + siblingCount * 2 + 2
      ),
      endPages.length > 0 ? endPages[0] - 2 : totalPages - 1
    );

    const items = [...startPages];

    if (siblingsStart > boundaryCount + 2) {
      items.push('ellipsis');
    } else if (boundaryCount + 1 < totalPages - boundaryCount) {
      items.push(boundaryCount + 1);
    }

    items.push(...range(siblingsStart, siblingsEnd));

    if (siblingsEnd < totalPages - boundaryCount - 1) {
      items.push('ellipsis');
    } else if (totalPages - boundaryCount > boundaryCount) {
      items.push(totalPages - boundaryCount);
    }

    items.push(...endPages);
    return items;
  }

  return (
    <div className="survey">
      <SurveyDialog
        title={'my title'}
        isLoading={isDialogLoading || isSectorsLoading}
        isOpen={isDialogOpen}
        close={closeDialog}
        error={dialogError || (sectorsError ? String(sectorsError) : undefined)}
        confirm={
          dialogContent === 'compare'
            ? compareSessionConfirmHandler
            : dialogContent === 'edit'
              ? editSessionConfirmHandler
              : shareSessionConfirmHandler
        }
      >
        {dialogContent === 'compare' && compareSessionDialog()}
        {dialogContent === 'edit' && editSessionDialog()}
        {dialogContent === 'share' && shareSessionDialog()}
      </SurveyDialog>

      <div className="survey-top">
        <div className="survey-badge" aria-hidden="true">
          <img src="/icon-graph.png" alt="" />
        </div>
        <h1 className="survey-title">{t('survey.start.title')}</h1>
        <p className="survey-subtitle">
          <Trans i18nKey="survey.start.subtitle" />
        </p>
        <div className="survey-actions">
          <Link to="/survey/start" style={{ textDecoration: 'none' }}>
            <button className="survey-cta" type="button">
              {t('survey.create.button')}
            </button>
          </Link>
        </div>
      </div>

      <div className="survey-container">
        <div className="survey-header" ref={headerRef}>
          <div className="survey-title-second">
            <div
              className={`survey-badge-target ${totalResults === 0 ? 'no-results' : ''}`}
              aria-hidden="true"
            >
              <img src="/icon-arrow-target.png" alt="" />
            </div>
            {t('survey.list.title')}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <TextField
              placeholder={t('survey.list.search')}
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.trim())}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData('text') ?? '';
                const normalized = pasted.trim();
                if (pasted !== normalized) {
                  e.preventDefault();
                  const input = e.target;
                  const start = input.selectionStart ?? 0;
                  const end = input.selectionEnd ?? 0;
                  const next = searchQuery.slice(0, start) + normalized + searchQuery.slice(end);
                  setSearchQuery(next);
                }
              }}
              sx={{ minWidth: 320 }}
              InputProps={{
                sx: {
                  borderRadius: '9999px',
                  height: '44px',
                  paddingRight: '40px'
                },
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: '8px' }}>
                    <SearchIcon sx={{ fontSize: 30, color: '#0A93D2' }} />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                sx: {
                  top: '-4px'
                }
              }}
            />

            <StatusFilterButton
              statuses={statuses}
              selectedStatuses={selectedStatuses}
              onToggle={toggleStatus}
            />
          </div>
        </div>

        {totalResults === 0 && !isLoading && (
          <div className="empty-state">
            Vous n’avez pas encore de Digiscore
          </div>
        )}

        <div className="result-count">
          {totalResults === 0 ? null : (
            totalResults > 1 ? (
              <>
                <span style={{ fontWeight: 'bold' }}>{totalResults}</span>{' '}
                <Trans i18nKey="survey.list.resultsFoundPlural" />
              </>
            ) : (
              <>
                <span style={{ fontWeight: 'bold' }}>{totalResults}</span>{' '}
                <Trans i18nKey="survey.list.resultsFoundSingular" />
              </>
            )
          )}
        </div>

        <div
          className="survey-list__container survey-list__container--cards"
          ref={listTopRef}
        >
          {isLoading ? (
            <Loading />
          ) : (
            totalResults > 0 && (
              <>
                {pagedItems.map((item, i) => (
                  <SurveyItem
                    key={i}
                    data={item.surveySessions}
                    enterpriseName={item.name}
                    bce={item.bce}
                    sector={t(`sector.${item.sectorCode}`)}
                    sectorCode={item.sectorCode}
                    namespace={item.namespace}
                    currentUser={user}
                    handleEditProfile={handleEditProfile}
                    handleCompareSession={handleCompareSession}
                    handleShareSession={handleShareSession}
                    handleModifySession={handleModifySession}
                  />
                ))}

                {totalResults > PAGE_LIMIT && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 8,
                      marginTop: 24,
                      flexWrap: 'wrap',
                      alignItems: 'center'
                    }}
                  >
                    {(() => {
                      const totalPages = Math.ceil(totalResults / PAGE_LIMIT);
                      const items = buildPageItems(totalPages, page, { boundaryCount: 1, siblingCount: 1 });

                      const goTo = async (p) => {
                        const target = Math.min(Math.max(1, p), totalPages);

                        if (target !== page && !isLoading) {
                          setPage(target);
                          await fetchPage(target);

                          if (hasVisited.current) {
                            requestAnimationFrame(() => {
                              headerRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            });
                          } else {
                            hasVisited.current = true;
                          }
                        }
                      };

                      return (
                        <>
                          <button
                            onClick={() => goTo(1)}
                            disabled={page === 1 || isLoading}
                            style={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: 18,
                              border: '1px solid #0A93D2',
                              background: 'transparent',
                              color: '#0A93D2',
                              fontWeight: 600,
                              cursor: page === 1 ? 'default' : 'pointer'
                            }}
                            aria-label="Première page"
                          >
                            «
                          </button>

                          <button
                            onClick={() => goTo(page - 1)}
                            disabled={page === 1 || isLoading}
                            style={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: 18,
                              border: '1px solid #0A93D2',
                              background: 'transparent',
                              color: '#0A93D2',
                              fontWeight: 600,
                              cursor: page === 1 ? 'default' : 'pointer'
                            }}
                            aria-label="Page précédente"
                          >
                            ‹
                          </button>

                          {items.map((it, idx) => {
                            if (it === 'ellipsis') {
                              return (
                                <span
                                  key={`e-${idx}`}
                                  style={{ padding: '0 6px', color: '#6f7a87' }}
                                  aria-hidden="true"
                                >
                                  …
                                </span>
                              );
                            }
                            const isActive = it === page;
                            return (
                              <button
                                key={it}
                                onClick={() => goTo(it)}
                                disabled={isActive || isLoading}
                                style={{
                                  minWidth: 36,
                                  height: 36,
                                  borderRadius: 18,
                                  border: '1px solid #0A93D2',
                                  background: isActive ? '#0A93D2' : 'transparent',
                                  color: isActive ? '#fff' : '#0A93D2',
                                  fontWeight: 600,
                                  cursor: isActive ? 'default' : 'pointer'
                                }}
                                aria-current={isActive ? 'page' : undefined}
                                aria-label={`Aller à la page ${it}`}
                              >
                                {it}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => goTo(page + 1)}
                            disabled={page === totalPages || isLoading}
                            style={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: 18,
                              border: '1px solid #0A93D2',
                              background: 'transparent',
                              color: '#0A93D2',
                              fontWeight: 600,
                              cursor: page === totalPages ? 'default' : 'pointer'
                            }}
                            aria-label="Page suivante"
                          >
                            ›
                          </button>

                          <button
                            onClick={() => goTo(totalPages)}
                            disabled={page === totalPages || isLoading}
                            style={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: 18,
                              border: '1px solid #0A93D2',
                              background: 'transparent',
                              color: '#0A93D2',
                              fontWeight: 600,
                              cursor: page === totalPages ? 'default' : 'pointer'
                            }}
                            aria-label="Dernière page"
                          >
                            »
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>

      <section className="faq">
        <div className="faq-header">
          <div className="faq-badge" aria-hidden="true">
            <img src="/icon-bubble-blue.png" alt="" />
          </div>
          <span className="survey-title-second">{t('home.faq.title')}</span>
        </div>

        <div className="faq-table">
          <div className="faq-row faq-row--head">
            <div className="faq-col faq-col--q">{t('home.faq.questions')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answers')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question0')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer0')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question1')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer1')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question2')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer2')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question3')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer3')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question4')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer4')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question5')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer5')}</div>
          </div>

          <div className="faq-sep"></div>

          <div className="faq-row">
            <div className="faq-col faq-col--q">{t('home.faq.question6')}</div>
            <div className="faq-col faq-col--a">{t('home.faq.answer6')}</div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SurveyPage;